import { Link } from "react-router-dom";
import Footer from "@/components/feature/Footer";
import HeroCarousel from "@/components/feature/HeroCarousel";
import ProductCarousel from "@/components/feature/ProductCarousel";
import SmartSearch from "@/components/feature/SmartSearch";
import ChristmasBanner from "@/components/feature/ChristmasBanner";
import ChristmasGiftIdeas from "@/components/feature/ChristmasGiftIdeas";
import { categories } from "@/mocks/categories";
import useActiveProducts from "@/hooks/useActiveProducts";

const steps = [
  {
    icon: "ri-user-add-line",
    title: "Crea tu cuenta gratis",
    description:
      "Regístrate en segundos y elige tu rol: comprador o vendedor. Empieza a explorar el catálogo de inmediato.",
  },
  {
    icon: "ri-image-add-line",
    title: "Publica tus productos",
    description:
      "Sube fotos, escribe una descripción detallada y fija tu precio. Tu publicación estará lista en minutos.",
  },
  {
    icon: "ri-chat-smile-3-line",
    title: "Vende y contacta",
    description:
      "Recibe mensajes de compradores interesados, negocia de forma segura y cierra la venta sin complicaciones.",
  },
];

export default function Home() {
  const { products, featured, counts, loading, error, refresh } =
    useActiveProducts(24);

  const destacados = featured.length > 0 ? featured : products;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background-50 via-primary-50 to-primary-100/80 bg-fixed">
      <HeroCarousel products={featured} />

      {/* Buscador inteligente */}
      <div id="buscar" className="mx-auto -mt-6 max-w-3xl px-4 md:px-6">
        <SmartSearch variant="hero" />
      </div>

      {/* Banner navideño */}
      <div className="mt-6">
        <ChristmasBanner />
      </div>

      {/* Categorías (scroll horizontal en móvil) */}
      <section id="categorias" className="mx-auto max-w-7xl scroll-mt-20 px-4 pt-8 md:px-6 md:pt-12">
        <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
          Explora por categorías
        </h2>
        <div className="no-scrollbar -mx-4 mt-4 flex snap-x gap-3 overflow-x-auto px-4 md:mx-0 md:grid md:grid-cols-4 md:gap-5 md:px-0">
          {categories.map((cat) => {
            const count = counts[cat.name] ?? 0;
            return (
              <Link
                key={cat.id}
                to={`/categoria/${cat.id}`}
                className="glass flex min-w-[150px] snap-start cursor-pointer items-center gap-3 rounded-lg p-4 transition-all active:scale-[0.98] md:min-w-0 md:hover:-translate-y-1 md:hover:border-primary-200 md:hover:shadow-soft"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className={`${cat.icon} text-xl`}></i>
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground-900">
                    {cat.name}
                  </p>
                  <p className="text-xs text-foreground-500">
                    {loading
                      ? "Cargando..."
                      : `${count} ${count === 1 ? "anuncio" : "anuncios"}`}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Carruseles de productos reales */}
      <div id="destacados" className="mx-auto max-w-7xl scroll-mt-20">
        {error ? (
          <div className="mx-4 mt-8 flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 px-6 py-14 text-center md:mx-6">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100">
              <i className="ri-error-warning-line text-2xl text-accent-700"></i>
            </span>
            <p className="mt-4 text-sm font-semibold text-foreground-900">
              No pudimos cargar los anuncios
            </p>
            <p className="mt-1 max-w-sm text-sm text-foreground-500">{error}</p>
            <button
              type="button"
              onClick={refresh}
              className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Reintentar
            </button>
          </div>
        ) : loading ? (
          <ProductCarousel title="Productos destacados" products={[]} loading />
        ) : products.length === 0 ? (
          <div className="mx-4 mt-8 flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 px-6 py-14 text-center md:mx-6">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100">
              <i className="ri-store-2-line text-2xl text-primary-600"></i>
            </span>
            <p className="mt-4 text-sm font-semibold text-foreground-900">
              Aún no hay anuncios publicados
            </p>
            <p className="mt-1 max-w-sm text-sm text-foreground-500">
              Sé el primero en publicar y tu anuncio aparecerá aquí en cuanto sea
              aprobado.
            </p>
            <Link
              to="/seller/dashboard"
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Publicar un anuncio
            </Link>
          </div>
        ) : (
          <>
            <ProductCarousel
              title="Productos destacados"
              products={destacados}
              to="/categoria/electronica"
            />
            <ChristmasGiftIdeas products={products} featured={featured} />
          </>
        )}
      </div>

      {/* Cómo funciona */}
      <section id="como-funciona" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-10 md:px-6 md:py-16">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            ¿Cómo funciona VendeYa?
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-foreground-500 md:text-base">
            Comprar y vender nunca fue tan sencillo. Tres pasos y listo.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {steps.map((step, index) => (
            <div
              key={step.title}
              className="glass glass-sheen relative rounded-lg p-6"
            >
              <span className="absolute right-5 top-5 font-heading text-4xl font-bold text-background-200">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className={`${step.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950 md:text-lg">
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-12 md:px-6 md:pb-16">
        <div className="relative overflow-hidden rounded-2xl bg-primary-600 px-6 py-12 text-center md:px-12 md:py-16">
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary-400/30 blur-3xl"></div>
          <div className="absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-accent-400/20 blur-3xl"></div>
          <div className="relative z-10">
            <h2 className="font-heading text-2xl font-bold text-white md:text-3xl">
              ¿Tienes algo que vender?
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-sm text-white/85 md:text-base">
              Publica tu primer anuncio gratis y llega a miles de compradores en
              tu ciudad. Sin comisiones ocultas.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/seller/dashboard"
                className="w-full cursor-pointer whitespace-nowrap rounded-full bg-white px-6 py-3 text-sm font-semibold text-primary-600 transition-colors hover:bg-background-100 sm:w-auto"
              >
                Empezar a vender
              </Link>
              <a
                href="#como-funciona"
                className="w-full cursor-pointer whitespace-nowrap rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10 sm:w-auto"
              >
                Ver cómo funciona
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}